import React, { useLayoutEffect, useRef } from 'react';
import { defineElement, LordIconElement } from '@lordicon/element';

const decodeLordicon = (encoded) => {
  const binary = atob(encoded.trim());
  let decoded = '';
  for (let index = 0; index < binary.length; index += 1) {
    decoded += String.fromCharCode(binary.charCodeAt(index) ^ 42);
  }
  return JSON.parse(decoded);
};

class StudentLordIcon extends LordIconElement {
  async loadIcon(src, signal) {
    const response = await fetch(src, { signal });
    if (!response.ok) {
      throw new Error(`Could not load icon ${src}: ${response.status}`);
    }
    const text = await response.text();
    if (text.trim().startsWith('{')) return JSON.parse(text);
    return decodeLordicon(text);
  }
}

if (typeof window !== 'undefined' && !window.customElements?.get('lord-icon')) {
  defineElement({ element: StudentLordIcon });
}

const MEDIA = 'https://media.lordicon.com/icons';

const ICONS = {
  home: 'system/outline/63-home',
  learn: 'system/outline/4092-book',
  job: 'system/outline/187-briefcase',
  users: 'system/outline/314-avatars-group',
  user: 'system/outline/44-avatar-circle',
  bell: 'system/outline/193-bell',
  search: 'system/outline/19-magnifier',
  next: 'system/outline/31-chevron-right',
  back: 'system/outline/32-chevron-left',
  flame: 'wired/outline/2443-blazing-flame',
  star: 'system/outline/237-star',
  trophy: 'system/outline/433-trophy-star',
  award: 'system/outline/4232-medal',
  share: 'system/outline/75-share',
  heart: 'system/outline/20-heart',
  msg: 'system/outline/3114-chat',
  pin: 'system/outline/18-location-pin',
  camera: 'system/outline/61-camera',
  file: 'system/outline/56-file-text',
  mega: 'system/outline/2873-megaphone',
  code: 'system/outline/369-code',
  chart: 'system/outline/153-bar-chart-vertical-grow',
  mic: 'system/outline/188-microphone',
  table: 'system/outline/365-grid-sections',
  zap: 'system/outline/451-bolt',
  target: 'system/outline/458-target-center',
  bulb: 'system/outline/36-bulb',
  filter: 'system/outline/736-filter',
  robot: 'wired/outline/461-robot',
  palette: 'system/outline/118-brush',
  ball: 'wired/outline/431-ball-soccer',
  phone: 'system/outline/140-phone',
  insta: 'https://media.lordicon.com/assets/icons/brands/instagram.json',
  dl: 'system/outline/199-download',
  cal: 'system/outline/28-calendar',
  crown: 'system/outline/407-crown',
  leaf: 'system/outline/586-plant',
  logo: 'system/outline/2474-sparkles',
  menu: 'system/outline/4172-menu-hamburger',
  close: 'system/outline/38-cross',
};

const HOVER_HOST = '.sa-card, .sa-tile, .sa-lvl, .sa-streak, .sa-logo, .sa-menu-item, .sa-hero, .sa-badge, .sa-stat, button, a';

const stateName = (state) => state?.name || state;

const Icon = ({ name, className = '' }) => {
  const nodeRef = useRef(null);
  const icon = ICONS[name];
  const decorative = className.includes('float') || name === 'flame';

  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) return undefined;

    node.setAttribute('class', `sa-i ${className}`.trim());
    const host = node.closest(HOVER_HOST);
    if (host) node.target = host;
    if (decorative) return undefined;

    const playIntro = () => {
      const names = [...(node.states || [])].map(stateName);
      const introState = names.find((item) => String(item).startsWith('in-'))
        || names.find((item) => String(item).startsWith('hover-'));
      if (!introState) return;
      node.play({ state: introState }).then((completed) => {
        if (completed === false || !node.player) return;
        node.player.state = node.state;
      });
    };

    node.addEventListener('ready', playIntro);
    if (node.matches(':state(ready)')) playIntro();
    return () => node.removeEventListener('ready', playIntro);
  }, [className, decorative]);

  if (!icon) return null;

  const src = icon.startsWith('http') ? icon : `${MEDIA}/${icon}.li`;

  return (
    <lord-icon
      ref={nodeRef}
      src={src}
      trigger={decorative ? 'loop' : 'hover'}
      current-color=""
      aria-hidden="true"
    />
  );
};

export default Icon;
